import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { Observable, tap, catchError, throwError, of } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  ImpersonationOption,
  ImpersonationOptionsResponse,
  ImpersonationResponse,
  GetImpersonationResponse,
  SetImpersonationRequest
} from '../models/admin.model';

@Injectable({
  providedIn: 'root'
})
export class AdminService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiBaseUrl}Admin/`;

  // Signals for impersonation state
  impersonationOptions = signal<ImpersonationOption[]>([]);
  currentSessionId = signal<string | null>(null);
  currentCompanyCode = signal<string | null>(null);


  private readonly SESSION_ID_KEY = 'impersonation_session_id';
  private readonly COMPANY_CODE_KEY = 'impersonation_company_code';
  private readonly COMPANY_NAME_KEY = 'impersonation_company_name';

  constructor() {
    this.initializeFromStorage();
  }

  private initializeFromStorage(): void {
    this.syncFromStorage();
  }

  syncFromStorage(): void {
    const sessionId = sessionStorage.getItem(this.SESSION_ID_KEY);
    const companyCode = sessionStorage.getItem(this.COMPANY_CODE_KEY);

    if (sessionId) {
      this.currentSessionId.set(sessionId);
    }
    if (companyCode) {
      this.currentCompanyCode.set(companyCode);
    }
  }

  /**
   * Get all available impersonation options for admin users
   * GET /api/Admin/impersonation/options
   * Returns cached options if available (options don't change during session)
   */
  getImpersonationOptions(forceRefresh = false): Observable<ImpersonationOptionsResponse> {
    // Return cached options if available and not forcing refresh
    const cachedOptions = this.impersonationOptions();
    if (!forceRefresh && cachedOptions.length > 0) {
      return of({ options: cachedOptions });
    }

    return this.http.get<ImpersonationOptionsResponse>(`${this.baseUrl}impersonation/options`).pipe(
      tap((response) => {
        this.impersonationOptions.set(response.options || []);
      }),
      catchError((error) => {
        console.error('Error fetching impersonation options:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Create a new impersonation session for this browser tab
   * POST /api/Admin/impersonation/session
   * The backend generates the UUID and returns it in the response body.
   * New sessions always start as ALL (null).
   */
  createImpersonationSession(): Observable<ImpersonationResponse> {
    return this.http.post<ImpersonationResponse>(`${this.baseUrl}impersonation/session`, {}).pipe(
      tap((response) => {
        // Store the backend-generated session ID so the interceptor sends it on every request
        sessionStorage.setItem(this.SESSION_ID_KEY, response.sessionId);
        this.currentSessionId.set(response.sessionId);
        // New session always starts as ALL — clear any stale company from a previous tab session
        this.currentCompanyCode.set(null);
        sessionStorage.removeItem(this.COMPANY_CODE_KEY);
        sessionStorage.removeItem(this.COMPANY_NAME_KEY);
      }),
      catchError((error) => {
        console.error('Error creating impersonation session:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Set the CompanyCode for an existing impersonation session
   * PUT /api/Admin/impersonation/session/{sessionId}
   */
  setImpersonation(sessionId: string, cardCode: string | null): Observable<void> {
    const request: SetImpersonationRequest = { cardCode };
    return this.http.put<void>(`${this.baseUrl}impersonation/session/${sessionId}`, request).pipe(
      tap(() => {
        this.currentCompanyCode.set(cardCode);
        // Store cardCode - use empty string for "ALL" to distinguish from "not set"
        const valueToStore = cardCode || '';
        sessionStorage.setItem(this.COMPANY_CODE_KEY, valueToStore);
      }),
      catchError((error) => {
        console.error('Error setting impersonation:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Get the current impersonation settings for a session
   * GET /api/Admin/impersonation/session/{sessionId}
   * Returns the CompanyCode being impersonated (null means "ALL")
   */
  getImpersonation(sessionId: string): Observable<GetImpersonationResponse> {
    return this.http.get<GetImpersonationResponse>(`${this.baseUrl}impersonation/session/${sessionId}`).pipe(
      tap((response) => {
        this.currentCompanyCode.set(response.cardCode);
        if (response.cardCode) {
          sessionStorage.setItem(this.COMPANY_CODE_KEY, response.cardCode);
        } else {
          sessionStorage.removeItem(this.COMPANY_CODE_KEY);
        }
      }),
      catchError((error) => {
        console.error('Error getting impersonation:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Clear impersonation data on logout
   */
  clearImpersonation(): void {
    this.currentSessionId.set(null);
    this.currentCompanyCode.set(null);
    this.impersonationOptions.set([]);
    sessionStorage.removeItem(this.SESSION_ID_KEY);
    sessionStorage.removeItem(this.COMPANY_CODE_KEY);
    sessionStorage.removeItem(this.COMPANY_NAME_KEY);
    // Clear pending navigation to avoid unwanted redirects after re-login
    sessionStorage.removeItem('pending_claim_navigation');
    // Clear user selection flag
    sessionStorage.removeItem('user_selected_company');
  }
}
